import {
  AdminSubscriptionRefundRequestDto,
  AdminUpdateSubscriptionRefundRequestDto,
  AstroApiClient,
  CreateSubscriptionRefundRequestDto,
  SubscriptionRefundRequestStatus as ApiSubscriptionRefundRequestStatus,
  SubscriptionRefundRequestEligibilityDto,
  SubscriptionRefundRequestSummaryDto,
} from '../api'

export type SubscriptionRefundRequestStatus = 'Pending' | 'Processed' | 'Rejected'

export type SubscriptionRefundRequestEligibilityStatus =
  | 'Eligible'
  | 'AlreadyRequested'
  | 'NoActiveProSubscription'
  | 'WindowExpired'
  | 'PaymentNotRefundable'

export interface SupportContact {
  companyName: string
  supportEmail: string
  supportPhone?: string | null
  websiteUrl?: string | null
}

export interface SubscriptionRefundRequestSummary {
  id: string
  contactEmail: string
  reason: string
  status: SubscriptionRefundRequestStatus
  createdAtUtc: string
  eligibleUntilUtc: string
  processedAtUtc?: string | null
  adminNotes?: string | null
}

export interface SubscriptionRefundRequestEligibility {
  canSubmit: boolean
  status: SubscriptionRefundRequestEligibilityStatus
  subscriptionId?: string | null
  planCode?: string | null
  planName?: string | null
  startsAtUtc?: string | null
  eligibleUntilUtc?: string | null
  eligibilityWindowDays: number
  refundReasonRequired: boolean
  publicPolicyUrl: string
  officialContactEmail: string
  contact: SupportContact
  existingRequest?: SubscriptionRefundRequestSummary | null
}

export interface CreateSubscriptionRefundRequestPayload {
  contactEmail: string
  reason: string
}

export interface AdminSubscriptionRefundRequest {
  id: string
  userId: string
  userName: string
  userPublicId?: string | null
  contactEmail: string
  reason: string
  status: SubscriptionRefundRequestStatus
  userSubscriptionId: string
  planCode?: string | null
  planName?: string | null
  orderId?: string | null
  paymentId?: string | null
  createdAtUtc: string
  eligibleUntilUtc: string
  processedAtUtc?: string | null
  adminNotes?: string | null
}

export interface PagedResult<T> {
  items: T[]
  page: number
  pageSize: number
  totalCount: number
}

export interface UpdateAdminSubscriptionRefundRequestPayload {
  status: SubscriptionRefundRequestStatus
  adminNotes?: string
}

function mapSummary(dto?: SubscriptionRefundRequestSummaryDto | null): SubscriptionRefundRequestSummary {
  return {
    id: dto?.id ?? '',
    contactEmail: dto?.contactEmail ?? '',
    reason: dto?.reason ?? '',
    status: (dto?.status ?? 'Pending') as SubscriptionRefundRequestStatus,
    createdAtUtc: dto?.createdAtUtc?.toISOString() ?? '',
    eligibleUntilUtc: dto?.eligibleUntilUtc?.toISOString() ?? '',
    processedAtUtc: dto?.processedAtUtc?.toISOString() ?? null,
    adminNotes: dto?.adminNotes ?? null,
  }
}

function mapEligibility(dto?: SubscriptionRefundRequestEligibilityDto | null): SubscriptionRefundRequestEligibility {
  return {
    canSubmit: !!dto?.canSubmit,
    status: (dto?.status ?? 'NoActiveProSubscription') as SubscriptionRefundRequestEligibilityStatus,
    subscriptionId: dto?.subscriptionId ?? null,
    planCode: dto?.planCode ?? null,
    planName: dto?.planName ?? null,
    startsAtUtc: dto?.startsAtUtc?.toISOString() ?? null,
    eligibleUntilUtc: dto?.eligibleUntilUtc?.toISOString() ?? null,
    eligibilityWindowDays: dto?.eligibilityWindowDays ?? 14,
    refundReasonRequired: dto?.refundReasonRequired ?? true,
    publicPolicyUrl: dto?.publicPolicyUrl ?? '',
    officialContactEmail: dto?.officialContactEmail ?? dto?.contact?.supportEmail ?? '',
    contact: {
      companyName: dto?.contact?.companyName ?? '',
      supportEmail: dto?.contact?.supportEmail ?? '',
      supportPhone: dto?.contact?.supportPhone ?? null,
      websiteUrl: dto?.contact?.websiteUrl ?? null,
    },
    existingRequest: dto?.existingRequest ? mapSummary(dto.existingRequest) : null,
  }
}

function mapAdminRequest(dto?: AdminSubscriptionRefundRequestDto | null): AdminSubscriptionRefundRequest {
  return {
    id: dto?.id ?? '',
    userId: dto?.userId ?? '',
    userName: dto?.userName ?? '',
    userPublicId: dto?.userPublicId ?? null,
    contactEmail: dto?.contactEmail ?? '',
    reason: dto?.reason ?? '',
    status: (dto?.status ?? 'Pending') as SubscriptionRefundRequestStatus,
    userSubscriptionId: dto?.userSubscriptionId ?? '',
    planCode: dto?.planCode ?? null,
    planName: dto?.planName ?? null,
    orderId: dto?.orderId ?? null,
    paymentId: dto?.paymentId ?? null,
    createdAtUtc: dto?.createdAtUtc?.toISOString() ?? '',
    eligibleUntilUtc: dto?.eligibleUntilUtc?.toISOString() ?? '',
    processedAtUtc: dto?.processedAtUtc?.toISOString() ?? null,
    adminNotes: dto?.adminNotes ?? null,
  }
}

export async function getSubscriptionRefundRequestEligibility(api: AstroApiClient) {
  const dto = await api.apiSubscriptionRefundRequestGet()
  return mapEligibility(dto)
}

export async function createSubscriptionRefundRequest(api: AstroApiClient, payload: CreateSubscriptionRefundRequestPayload) {
  const dto = await api.apiSubscriptionRefundRequestPost(new CreateSubscriptionRefundRequestDto(payload))
  return mapSummary(dto)
}

export async function getAdminSubscriptionRefundRequests(api: AstroApiClient, page: number, pageSize: number) {
  const dto = await api.apiAdminUsersRefundRequestsGet(page, pageSize)

  return {
    items: (dto?.items ?? []).map(mapAdminRequest),
    page: dto?.page ?? page,
    pageSize: dto?.pageSize ?? pageSize,
    totalCount: dto?.totalCount ?? 0,
  } satisfies PagedResult<AdminSubscriptionRefundRequest>
}

export async function updateAdminSubscriptionRefundRequest(
  api: AstroApiClient,
  id: string,
  payload: UpdateAdminSubscriptionRefundRequestPayload,
) {
  const body = new AdminUpdateSubscriptionRefundRequestDto({
    adminNotes: payload.adminNotes,
    status: payload.status as unknown as ApiSubscriptionRefundRequestStatus,
  })
  const dto = await api.apiAdminUsersRefundRequestsPut(id, body)
  return mapAdminRequest(dto)
}
